import * as React from "react";
import { useThunkDispatch } from "../../store";
import { reportParserError, finalizeParse } from "../../store/diagnostics/actions";
import { logger, SeverityLevel } from "../services/loggingService";

/**
 * Wires a `JsxParser` instance into the diagnostics store.
 *
 * Returns an `onError` handler that records each parser error, and runs a post-commit effect
 * that finalizes the parse pass — clearing stale diagnostics when a previously-broken template
 * now parses cleanly.  The template `source` is used as the parse identity (the parser re-parses
 * whenever it changes), and the bucket is keyed by `fileName` (or the source for inline templates).
 *
 * @param fileName - The template file name, or `undefined` for inline templates.
 * @param source - The template source string passed to the `JsxParser`.
 */
export function useTemplateDiagnostics(fileName: string | undefined, source: string | undefined) {
    const dispatch = useThunkDispatch();

    const onError = React.useCallback((e: Error) => {
        dispatch(reportParserError(fileName, source, e));
    }, [fileName, source]);

    React.useEffect(() => {
        if (source != null) {
            dispatch(finalizeParse(fileName ?? source, source));
        }
    }, [fileName, source]);

    return onError;
}
